import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Code2, MessageSquare, GitPullRequest, Users, Lightbulb, CheckCircle2,
  TrendingUp, AlertCircle, Clock, Zap, Activity, FlaskConical, FileText,
  Search, Target, Sparkles, AppWindow, ArrowRight, ArrowLeftRight,
} from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

const EASE = [0.16, 1, 0.3, 1];

const CATEGORY = {
  Development:   { color: "#5B5BD6", icon: Code2 },
  Review:        { color: "#8B5CF6", icon: GitPullRequest },
  Communication: { color: "#0EA5E9", icon: MessageSquare },
  Research:      { color: "#14B8A6", icon: Search },
  Testing:       { color: "#F59E0B", icon: FlaskConical },
  Planning:      { color: "#EC4899", icon: Target },
  Documentation: { color: "#10B981", icon: FileText },
};
const catMeta = (n) => CATEGORY[n] || { color: "#64748B", icon: Activity };

const HL_ICONS = { code: Code2, review: GitPullRequest, collab: Users, message: MessageSquare, idea: Lightbulb, check: CheckCircle2, trend: TrendingUp, activity: Activity };
const INTENSITY = {
  high:   { label: "Deep focus", color: "#5B5BD6", bg: "rgba(91,91,214,0.10)" },
  medium: { label: "Steady",     color: "#0EA5E9", bg: "rgba(14,165,233,0.10)" },
  low:    { label: "Light",      color: "#10B981", bg: "rgba(16,185,129,0.10)" },
};
const INSIGHT = {
  positive: { color: "#16A34A", bg: "rgba(22,163,74,0.08)", ring: "rgba(22,163,74,0.22)", icon: CheckCircle2, tag: "Strength" },
  tip:      { color: "#D97706", bg: "rgba(217,119,6,0.08)", ring: "rgba(217,119,6,0.22)", icon: Lightbulb,    tag: "Tip" },
};

// ── Hourly metrics strip (from `metrics` event) ─────────────────────────────
export function HourlyMetricsStrip({ metrics, streaming }) {
  const t = useDashTheme();
  if (!metrics) return null;
  const { activeMinutes, segmentCount, projectCount, appBreakdown = [], topApps = [], copyFlows = [] } = metrics;
  const ACCENT = ["#5B5BD6", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];
  const total = appBreakdown.reduce((s, d) => s + d.hours, 0) || 1;

  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        <Kpi t={t} icon={<Clock size={16} />} label="Active" value={`${activeMinutes}m`} color="#5B5BD6" bg="rgba(91,91,214,0.11)" streaming={streaming} />
        <Kpi t={t} icon={<Activity size={16} />} label="Segments" value={segmentCount} color="#0EA5E9" bg="rgba(14,165,233,0.11)" />
        <Kpi t={t} icon={<AppWindow size={16} />} label="Apps" value={topApps.length || projectCount} color="#10B981" bg="rgba(16,185,129,0.11)" />
      </div>
      {/* App mix bar */}
      {appBreakdown.length > 0 && (
        <div style={{ borderRadius: 14, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 8px ${t.c.shadowColor}`, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <AppWindow size={12} style={{ color: t.c.textTertiary }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em" }}>App mix this hour</span>
          </div>
          <div style={{ display: "flex", height: 8, borderRadius: 99, overflow: "hidden", gap: 2, marginBottom: 8 }}>
            {appBreakdown.slice(0, 6).map((d, i) => (
              <motion.div key={i} initial={{ flexGrow: 0 }} animate={{ flexGrow: (d.hours || 0.01) / total }}
                transition={{ delay: 0.1 + i * 0.05, duration: 0.5, ease: EASE }}
                style={{ flexBasis: 0, background: ACCENT[i % ACCENT.length], borderRadius: 2 }} />
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
            {appBreakdown.slice(0, 5).map((d, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: ACCENT[i % ACCENT.length] }} />
                <span style={{ fontSize: 12, color: t.c.textSecondary }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.c.text }}>{Math.round((d.hours / total) * 100)}%</span>
              </span>
            ))}
          </div>
        </div>
      )}
      {/* Copy flows */}
      {copyFlows.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <ArrowLeftRight size={12} /> Data movement
          </span>
          {copyFlows.map((c, i) => {
            const [src, dst] = c.flow.split(" → ");
            return (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 99, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, fontSize: 11.5, fontWeight: 600, color: t.c.text }}>
                {src} <ArrowRight size={11} style={{ color: "#5B5BD6" }} /> {dst}{c.count > 1 ? ` ·${c.count}` : ""}
              </span>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function Kpi({ t, icon, label, value, color, bg, streaming }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, ease: EASE }}
      style={{ borderRadius: 14, padding: "14px 16px", background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 8px ${t.c.shadowColor}`, display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ display: "inline-flex", width: 34, height: 34, borderRadius: 10, alignItems: "center", justifyContent: "center", background: bg, color }}>{icon}</span>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: t.c.text, letterSpacing: "-0.03em", lineHeight: 1.1, fontVariantNumeric: "tabular-nums", display: "flex", alignItems: "center", gap: 5 }}>
          {value}
          {streaming && label === "Active" && (
            <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.1, repeat: Infinity }}
              style={{ width: 5, height: 5, borderRadius: "50%", background: color, display: "inline-block" }} />
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Hourly structured report (micro-segment timeline) ───────────────────────
export function HourlyReportRenderer({ report, streaming, partial = false }) {
  const t = useDashTheme();
  if (!report) return null;
  const { headline, overallSentiment, segments = [], activityBreakdown = [], highlights = [], insights = [], rawText } = report;
  const writing = partial || streaming;

  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Headline */}
      <AnimatePresence>
        {headline && (
          <motion.div key="h" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
            style={{ position: "relative", overflow: "hidden", borderRadius: 16, padding: "18px 22px", background: "linear-gradient(120deg, #6366F1 0%, #5B5BD6 55%, #7C3AED 100%)", boxShadow: "0 10px 32px rgba(91,91,214,0.36)", color: "#fff" }}>
            <div style={{ position: "absolute", top: -30, right: -20, width: 140, height: 140, borderRadius: "50%", background: "rgba(255,255,255,0.10)", filter: "blur(24px)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8, opacity: 0.85 }}>
                <Zap size={12} /><span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em" }}>Hourly intelligence</span>
                {writing && <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} style={{ fontSize: 10.5, opacity: 0.8 }}>· writing…</motion.span>}
              </div>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700, lineHeight: 1.45, letterSpacing: "-0.01em" }}>{headline}</p>
              {overallSentiment && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5, marginTop: 10, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: "rgba(255,255,255,0.20)" }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />
                  {overallSentiment === "positive" ? "Productive hour" : overallSentiment === "mixed" ? "Mixed hour" : "Steady hour"}
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Activity breakdown */}
      <AnimatePresence>
        {activityBreakdown.length > 0 && (
          <Panel key="ab" t={t} accent="#5B5BD6" icon={<Activity size={14} />} title="How the hour split">
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[...activityBreakdown].sort((a, b) => b.percentage - a.percentage).map((d, i) => {
                const { color, icon: Icon } = catMeta(d.category);
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ display: "inline-flex", width: 30, height: 30, borderRadius: 9, alignItems: "center", justifyContent: "center", background: `${color}18`, color }}><Icon size={14} /></span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: t.c.text }}>{d.category}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 800, color }}>{d.percentage}%</span>
                      </div>
                      <div style={{ height: 7, borderRadius: 99, background: t.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", overflow: "hidden" }}>
                        <motion.div initial={{ width: 0 }} animate={{ width: `${d.percentage}%` }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}
                          style={{ height: "100%", borderRadius: 99, background: `linear-gradient(90deg, ${color}, ${color}CC)` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {/* Micro-segment timeline */}
      <AnimatePresence>
        {segments.length > 0 && (
          <Panel key="seg" t={t} accent="#0EA5E9" icon={<Clock size={14} />} title="Minute-by-minute"
            subtitle={`${segments.length} ${segments.length === 1 ? "segment" : "segments"}${writing ? " · writing…" : ""}`}>
            <motion.div layout style={{ display: "flex", flexDirection: "column" }}>
              <AnimatePresence initial={false}>
                {segments.map((s, i) => <Segment key={`${i}-${s.timeRange}`} s={s} t={t} isLast={i === segments.length - 1 && !writing} />)}
              </AnimatePresence>
              {writing && <SegShimmer t={t} />}
            </motion.div>
          </Panel>
        )}
      </AnimatePresence>

      {/* Highlights */}
      <AnimatePresence>
        {highlights.length > 0 && (
          <Panel key="hl" t={t} accent="#16A34A" icon={<Sparkles size={14} />} title="Highlights">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
              <AnimatePresence initial={false}>
                {highlights.map((h, i) => {
                  const Icon = HL_ICONS[h.icon] || CheckCircle2;
                  return (
                    <motion.div key={h.title || i} layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }}
                      style={{ display: "flex", gap: 10, padding: "12px 14px", borderRadius: 12, background: t.isDark ? "rgba(22,163,74,0.07)" : "rgba(22,163,74,0.05)", border: "1px solid rgba(22,163,74,0.18)" }}>
                      <span style={{ color: "#16A34A", flexShrink: 0, marginTop: 1 }}><Icon size={14} /></span>
                      <div>
                        {h.title && <div style={{ fontSize: 12.5, fontWeight: 700, color: t.c.text, marginBottom: 2 }}>{h.title}</div>}
                        <div style={{ fontSize: 12, color: t.c.textSecondary, lineHeight: 1.5 }}>{h.text}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {/* Insights */}
      <AnimatePresence>
        {insights.length > 0 && (
          <Panel key="in" t={t} accent="#F59E0B" icon={<Lightbulb size={14} />} title="Insights">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
              <AnimatePresence initial={false}>
                {insights.map((ins, i) => {
                  const m = INSIGHT[ins.type] || INSIGHT.tip; const Icon = m.icon;
                  return (
                    <motion.div key={ins.title || i} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }}
                      style={{ padding: "14px 16px", borderRadius: 12, background: m.bg, border: `1px solid ${m.ring}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                        <span style={{ display: "inline-flex", width: 24, height: 24, borderRadius: 7, alignItems: "center", justifyContent: "center", background: `${m.color}1E`, color: m.color }}><Icon size={13} /></span>
                        <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.07em", color: m.color }}>{m.tag}</span>
                        {ins.title && <span style={{ fontSize: 12.5, fontWeight: 700, color: t.c.text }}>· {ins.title}</span>}
                      </div>
                      <p style={{ margin: 0, fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55 }}>{ins.text}</p>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {rawText && segments.length === 0 && (
        <Panel t={t} accent="#5B5BD6" icon={<FileText size={14} />} title="Summary">
          <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{rawText}</p>
        </Panel>
      )}
    </motion.div>
  );
}

// ── Micro-segment row ────────────────────────────────────────────────────────
function Segment({ s, t, isLast }) {
  const meta = INTENSITY[s.intensity] || INTENSITY.medium;
  const { color, icon: Icon } = catMeta(s.category);
  const apps = s.apps || [];
  return (
    <motion.div layout initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ display: "flex", gap: 14 }}>
      {/* rail */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 18, flexShrink: 0, paddingTop: 4 }}>
        <span style={{ display: "inline-flex", width: 26, height: 26, borderRadius: 8, alignItems: "center", justifyContent: "center", background: `${color}1A`, color, flexShrink: 0 }}><Icon size={13} /></span>
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 16, background: `linear-gradient(${color}44, ${t.c.border})`, marginTop: 4, borderRadius: 2 }} />}
      </div>
      {/* card */}
      <div style={{ flex: 1, paddingBottom: isLast ? 0 : 14, minWidth: 0 }}>
        <div style={{ borderRadius: 12, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 1px 6px ${t.c.shadowColor}`, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>{s.timeRange}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em" }}>{s.title || "Work"}</span>
            </div>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: meta.bg, color: meta.color }}>
              <span style={{ width: 4, height: 4, borderRadius: "50%", background: meta.color }} />{meta.label}
            </span>
          </div>
          {s.summary && <p style={{ margin: "0 0 8px", fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55 }}>{s.summary}</p>}
          {apps.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {apps.map((a, i) => (
                <span key={i} style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 8px", borderRadius: 99, background: t.c.surfaceMuted, color: t.c.textSecondary, border: `1px solid ${t.c.border}` }}>{a}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function SegShimmer({ t }) {
  const bar = (w) => <motion.div animate={{ opacity: [0.35, 0.7, 0.35] }} transition={{ duration: 1.3, repeat: Infinity }} style={{ height: 9, width: w, borderRadius: 5, background: t.isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)" }} />;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ display: "flex", gap: 14 }}>
      <div style={{ width: 18, flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: 4 }}>
        <motion.span animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.2, repeat: Infinity }} style={{ width: 10, height: 10, borderRadius: "50%", background: "#0EA5E9" }} />
      </div>
      <div style={{ flex: 1, borderRadius: 12, background: t.c.surface, border: `1px solid ${t.c.border}`, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {bar("38%")}{bar("80%")}
      </div>
    </motion.div>
  );
}

function Panel({ t, icon, title, subtitle, accent, children }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ borderRadius: 16, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 10px ${t.c.shadowColor}`, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${t.c.border}` }}>
        <span style={{ display: "inline-flex", width: 28, height: 28, borderRadius: 9, alignItems: "center", justifyContent: "center", background: `${accent}16`, color: accent, flexShrink: 0 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: "16px 18px" }}>{children}</div>
    </motion.div>
  );
}

export default HourlyReportRenderer;
